/**
 * SupportingDocsPicker — compact multi-file uploader for entity dialogs.
 *
 * Used by New Claim / LD Tracker / Variation / Dispute / Correspondence
 * dialogs to attach evidence files.  Files are held locally until the parent
 * dialog decides how to persist them (typically: create the entity first, then
 * upload each file to `POST /contracts/entities/{type}/{id}/attach`).
 */
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Paperclip, X, FileText } from 'lucide-react';

export function SupportingDocsPicker({ files = [], onChange, testIdPrefix = 'supporting-docs' }) {
  const inputRef = useRef(null);
  const add = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    onChange([...(files || []), ...incoming]);
  };
  const remove = (idx) => onChange((files || []).filter((_, i) => i !== idx));

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.txt,.rtf,.md,.xlsx,.xls,.csv,.pptx,.ppt,.jpg,.jpeg,.png,.webp,.heic,.heif,.tiff"
          className="hidden"
          onChange={e => { add(e.target.files); e.target.value = ''; }}
          data-testid={`${testIdPrefix}-input`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          className="border-border text-foreground"
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Paperclip className="w-3.5 h-3.5 mr-1.5" />
          {(files || []).length ? `Add more (${files.length})` : 'Attach supporting docs'}
        </Button>
        {(files || []).length > 0 && (
          <span className="text-[10px] text-muted-foreground/70">Stored in object storage on save</span>
        )}
      </div>
      {(files || []).length > 0 && (
        <div className="space-y-1 max-h-32 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between text-xs rounded border border-border bg-muted px-2 py-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <FileText className="w-3 h-3 text-blue-500 shrink-0" />
                <span className="truncate text-foreground">{f.name}</span>
                <span className="text-[10px] text-muted-foreground/70 shrink-0">{Math.round(f.size / 1024)}KB</span>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-muted-foreground/70 hover:text-red-500 shrink-0"
                data-testid={`${testIdPrefix}-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SupportingDocsPicker;
